import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { REDUX_STORE_SLICES } from '../../services/exports/Constants';
import { SliceCaseReducers } from '@reduxjs/toolkit/dist/createSlice';
import { GiftCard } from '../../services/exports/Interfaces';
import { StoreInterface } from '../types';

/**
 * The discount an applied offer just displaced — the record that powers the *reverse* direction
 * of the displacement notice (FOO-2555 / PROD-407, PM video / Lovable prototype `previousPromo`).
 *
 * Forward direction ("Promo code CHEVERE applied. This removed the offer.") is derived from live
 * state — a promo/gift/points is *currently* suppressing an offer. The reverse direction ("Offer
 * 2-für-1 applied. This removed the CHEVERE promo code.") can't be derived: once the customer
 * chooses "Use offer instead", the promo/gift/points is cleared, so nothing is left in state to
 * name. This record captures it at that gesture so the notice can name it and Undo can restore it.
 */
export interface DisplacedByOffer {
  // An offer can only displace a promo code or a gift card — an offer and points coexist.
  kind: 'promo' | 'gift';
  /** Human label for the message — the promo code or a masked gift. */
  label: string;
  /** Promo code, for Undo re-apply. */
  promoCode?: string;
  /** The displaced gift card, for Undo re-apply. */
  giftCard?: GiftCard;
}

/**
 * The points redemption a just-applied promo code or gift card displaced (FOO-2555 / PROD-407).
 *
 * A promo code or gift card is exclusive with everything, so applying one clears an active points
 * redemption. That removal can't be derived after the fact (once cleared, nothing in state names
 * how many points were spent), so it's captured here at the apply gesture — the notice explains it
 * ("Promo code CHEVERE applied. This removed your points redemption.") and Undo restores the points.
 */
export interface CouponDisplacedPoints {
  // Only a promo code or a gift card displaces points — an offer and points coexist.
  kind: 'promo' | 'gift';
  /** The promo code, for the message; a gift card has no code (its label is static copy). */
  label: string;
  /** The points that were redeemed, for Undo re-apply. */
  points: number;
}

/**
 * The promo code / gift card a just-applied points redemption displaced (FOO-2555 / PROD-407, UX
 * feedback item 3). Points are exclusive with a promo/gift, so redeeming points clears an active
 * code. Captured at the "use points" gesture so the notice can name it + savings and Undo restores
 * it: "50 points applied (–5,00 €). Promo code DOMINOSP (–4,00 €) was removed. [Undo]".
 */
export interface PointsDisplacedCoupon {
  kind: 'promo' | 'gift';
  /** The promo code (for the message + Undo re-apply); empty for a gift card. */
  label: string;
  /** Pre-formatted € the displaced code was saving, when known (kept as a string so the notice
   *  doesn't need `formatCurrency`, which pulls in lottie and breaks under jsdom). */
  couponSaving?: string | null;
  /** Points redeemed (count) + their pre-formatted € value, for "N points applied (–€X)". */
  points: number;
  pointsSaving?: string | null;
  /** The displaced gift card, for Undo re-apply (a gift can't be re-fetched by code). */
  giftCard?: GiftCard;
}

type InitialState = Readonly<{
  displacedByOffer: DisplacedByOffer | null;
  couponDisplacedPoints: CouponDisplacedPoints | null;
  pointsDisplacedCoupon: PointsDisplacedCoupon | null;
}>;

const initialState: InitialState = {
  displacedByOffer: null,
  couponDisplacedPoints: null,
  pointsDisplacedCoupon: null,
} as const;

const discountDisplacementSlice = createSlice<InitialState, SliceCaseReducers<InitialState>>({
  name: REDUX_STORE_SLICES.DISCOUNT_DISPLACEMENT,
  initialState,
  reducers: {
    setDisplacedByOffer: (state, action: PayloadAction<DisplacedByOffer>) => {
      state.displacedByOffer = action.payload;
    },
    clear: (state) => {
      state.displacedByOffer = null;
    },
    setCouponDisplacedPoints: (state, action: PayloadAction<CouponDisplacedPoints>) => {
      state.couponDisplacedPoints = action.payload;
      state.pointsDisplacedCoupon = null;
    },
    clearCouponDisplacedPoints: (state) => {
      state.couponDisplacedPoints = null;
    },
    setPointsDisplacedCoupon: (state, action: PayloadAction<PointsDisplacedCoupon>) => {
      state.pointsDisplacedCoupon = action.payload;
      state.couponDisplacedPoints = null;
    },
    clearPointsDisplacedCoupon: (state) => {
      state.pointsDisplacedCoupon = null;
    },
  },
});

export const selectDisplacedByOffer = (state: StoreInterface) =>
  state[REDUX_STORE_SLICES.DISCOUNT_DISPLACEMENT]?.displacedByOffer ?? null;

export const selectCouponDisplacedPoints = (state: StoreInterface) =>
  state[REDUX_STORE_SLICES.DISCOUNT_DISPLACEMENT]?.couponDisplacedPoints ?? null;

export const selectPointsDisplacedCoupon = (state: StoreInterface) =>
  state[REDUX_STORE_SLICES.DISCOUNT_DISPLACEMENT]?.pointsDisplacedCoupon ?? null;

export default discountDisplacementSlice;
